import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Twitter, Linkedin, Facebook, Mail } from 'lucide-react';
import logo from '../../dark_logo.webp';
import api from '../../utils/api';

const Footer = () => {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');

    const handleSubscribe = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage('');

        try {
            await api.post('/marketing/subscribe', { email, source: 'footer' });
            setMessage('Success! Welcome to the community.');
            setEmail('');
        } catch (error) {
            setMessage(error.response?.data?.message || 'Something went wrong. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <footer className="bg-slate-100 border-t border-slate-200 dark:bg-slate-950 dark:border-slate-800 pt-16 pb-8 text-slate-700 dark:text-slate-200">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
                    <div className="col-span-1 md:col-span-1">
                        <div className="flex items-center gap-2 mb-6">
                            <img src={logo} alt="MedCoreX" width="200" height="80" className="h-28 w-auto" loading="lazy" />
                        </div>
                        <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed mb-6">
                            Clinical pharmacy workflows built and reviewed by practicing pharmacists—designed to improve safety, documentation quality, and therapy optimization.
                        </p>
                        <div className="flex gap-4">
                            <a href="#" aria-label="Twitter" className="text-slate-500 dark:text-slate-300 hover:text-emerald-600 dark:hover:text-emerald-300 transition-colors"><Twitter size={20} /></a>
                            <a href="#" aria-label="LinkedIn" className="text-slate-500 dark:text-slate-300 hover:text-emerald-600 dark:hover:text-emerald-300 transition-colors"><Linkedin size={20} /></a>
                            <a href="#" aria-label="Facebook" className="text-slate-500 dark:text-slate-300 hover:text-emerald-600 dark:hover:text-emerald-300 transition-colors"><Facebook size={20} /></a>
                        </div>
                    </div>

                    <div>
                        <h3 className="text-slate-900 dark:text-white font-semibold mb-6">Product</h3>
                        <ul className="space-y-3 text-sm">
                            <li><Link to="/features" className="text-slate-600 dark:text-slate-300 hover:text-emerald-600 dark:hover:text-emerald-300 transition-colors">Features</Link></li>
                            <li><Link to="/about" className="text-slate-600 dark:text-slate-300 hover:text-emerald-600 dark:hover:text-emerald-300 transition-colors">About Us</Link></li>
                            <li><Link to="/calculator" className="text-slate-600 dark:text-slate-300 hover:text-emerald-600 dark:hover:text-emerald-300 transition-colors">Clinical Calculator</Link></li>
                            <li><Link to="/pricing" className="text-slate-600 dark:text-slate-300 hover:text-emerald-600 dark:hover:text-emerald-300 transition-colors">Pricing</Link></li>
                        </ul>
                    </div>

                    <div>
                        <h3 className="text-slate-900 dark:text-white font-semibold mb-6">Support</h3>
                        <ul className="space-y-3 text-sm">
                            <li><Link to="/docs" className="text-slate-600 dark:text-slate-300 hover:text-emerald-600 dark:hover:text-emerald-300 transition-colors">Documentation</Link></li>
                            <li><Link to="/docs/api-reference" className="text-slate-600 dark:text-slate-300 hover:text-emerald-600 dark:hover:text-emerald-300 transition-colors">API Reference</Link></li>
                            <li><Link to="/contact" className="text-slate-600 dark:text-slate-300 hover:text-emerald-600 dark:hover:text-emerald-300 transition-colors">Contact Support</Link></li>
                            <li><Link to="/status" className="text-slate-600 dark:text-slate-300 hover:text-emerald-600 dark:hover:text-emerald-300 transition-colors">System Status</Link></li>
                        </ul>
                    </div>

                    <div>
                        <h3 className="text-slate-900 dark:text-white font-semibold mb-6">Stay Updated</h3>
                        <p className="text-sm text-slate-600 dark:text-slate-300 mb-4">
                            Subscribe to our newsletter to receive the latest clinical updates and features.
                        </p>
                        <form onSubmit={handleSubscribe} className="space-y-3">
                            <div className="relative">
                                <label htmlFor="footer-newsletter-email" className="sr-only">Email address for newsletter</label>
                                <input
                                    id="footer-newsletter-email"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="Enter your email"
                                    required
                                    className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg px-4 py-2.5 text-sm text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all"
                                />
                                <button
                                    type="submit"
                                    aria-label="Subscribe to newsletter"
                                    disabled={loading}
                                    className="absolute right-1 top-1 bottom-1 bg-emerald-500 hover:bg-emerald-600 text-slate-950 rounded-md px-3 text-xs font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {loading ? '...' : 'Join'}
                                </button>
                            </div>
                            {message && (
                                <p className={`text-xs ${message.includes('Success') || message.includes('Welcome') ? 'text-emerald-600 dark:text-emerald-300' : 'text-red-600 dark:text-red-300'}`}>
                                    {message}
                                </p>
                            )}
                        </form>

                        <div className="mt-8 pt-8 border-t border-slate-200 dark:border-slate-800">
                            <h3 className="text-slate-900 dark:text-white font-semibold mb-4">Contact</h3>
                            <a
                                href="mailto:support@medcorex.org?subject=Inquiry%20from%20MedCoreX"
                                className="group block bg-white dark:bg-slate-900/70 hover:bg-slate-50 dark:hover:bg-slate-900 border border-slate-200 dark:border-slate-800 hover:border-emerald-500/50 rounded-xl p-4 transition-all duration-300 hover:shadow-[0_0_20px_rgba(16,185,129,0.12)] dark:hover:shadow-[0_0_20px_rgba(16,185,129,0.15)]"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="p-2.5 bg-slate-100 dark:bg-slate-800 rounded-lg group-hover:bg-emerald-500/10 transition-colors duration-300">
                                        <Mail size={20} className="text-slate-600 dark:text-slate-300 group-hover:text-emerald-600 dark:group-hover:text-emerald-300 transition-colors duration-300" />
                                    </div>
                                    <div>
                                        <span className="block text-sm text-slate-600 dark:text-slate-300 font-medium mb-0.5 transition-colors">Email Us</span>
                                        <span className="block text-sm text-slate-900 dark:text-white font-bold group-hover:text-emerald-600 dark:group-hover:text-emerald-300 transition-colors duration-300">
                                            support@medcorex.org
                                        </span>
                                    </div>
                                </div>
                            </a>
                        </div>
                    </div>
                </div>

                <div className="pt-8 border-t border-slate-200 dark:border-slate-800 flex flex-col md:flex-row justify-between items-center text-xs text-slate-500 dark:text-slate-300">
                    <p>&copy; {new Date().getFullYear()} MedCoreX. All rights reserved.</p>
                    <div className="flex gap-6 mt-4 md:mt-0">
                        <Link to="/privacy" className="text-slate-600 dark:text-slate-300 hover:text-emerald-600 dark:hover:text-emerald-300 transition-colors">Privacy Policy</Link>
                        <Link to="/terms" className="text-slate-600 dark:text-slate-300 hover:text-emerald-600 dark:hover:text-emerald-300 transition-colors">Terms of Service</Link>
                        <Link to="/terms" className="text-slate-600 dark:text-slate-300 hover:text-emerald-600 dark:hover:text-emerald-300 transition-colors">Refund Policy</Link>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
